import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { setPromptNode } from "../../utils/promptStore";
import { withIntent } from "../../utils/postLoginRedirect";
import { PromptHighlight, BenefitList, BenefitItem, Checkmark } from "./styles";

export type SignupPrompt = {
  reason: string;
  surface: string;
  track_id?: string;
};

type GateIntent = { type: string; trackId: string };

const BENEFITS = [
  "Download unlimited .mp3s under Creative Commons",
  "Access to the multitrack editor for additional songs",
  "Create playlists and organize your music"
];

const SignupGatePrompt = () => (
  <>
    Logged-out download limit reached.{" "}
    <PromptHighlight>Create a free account</PromptHighlight> to:
    <BenefitList>
      {BENEFITS.map(text => (
        <BenefitItem key={text}>
          <Checkmark>✓</Checkmark>
          <span>{text}</span>
        </BenefitItem>
      ))}
    </BenefitList>
  </>
);

// signup modal copy for the guest-gated track actions
export const GATE_PROMPTS = {
  favorite: (
    <>
      Save your <PromptHighlight>favorites</PromptHighlight> as you browse —
      create a free account now
    </>
  ),
  playlist: (
    <>
      Create quick <PromptHighlight>collections</PromptHighlight> to organize
      your tracks. Create your <PromptHighlight>free account</PromptHighlight>
    </>
  ),
  editor: (
    <>
      Remix any track with the{" "}
      <PromptHighlight>Multitrack Editor</PromptHighlight> — create a free
      account to get started
    </>
  )
};

/**
 * open the signup modal over the current page
 * - `intent` ({ type, trackId }) resumes the gated action once the user is
 *   authenticated (see PendingIntentHandler); without it they just return here
 * - `signupPrompt` ({ reason, surface, track_id? }) is the analytics entry reason
 *   the auth form reports as `signup_prompted`
 */
export const pushSignupGate = (
  history: any,
  location: any,
  promptNode: React.ReactNode,
  intent: GateIntent | undefined,
  signupPrompt: SignupPrompt
) => {
  setPromptNode(promptNode);
  history.push("/signup", {
    background: location,
    redirect: intent ? withIntent(location, intent) : undefined,
    signupPrompt
  });
};

export const useSignupGate = () => {
  const history = useHistory();
  const location = useLocation();

  return useCallback(
    (
      promptNode: React.ReactNode,
      intent: GateIntent | undefined,
      signupPrompt: SignupPrompt
    ) => pushSignupGate(history, location, promptNode, intent, signupPrompt),
    [history, location]
  );
};

export const promptSignupGate = (
  history: any,
  location: any,
  intent: GateIntent | undefined,
  signupPrompt: SignupPrompt
) =>
  pushSignupGate(history, location, <SignupGatePrompt />, intent, signupPrompt);

export default SignupGatePrompt;
